import {
  EventPassFragment,
  FullEventFragment,
  useFeatureFlagsQuery,
  useRedeemEventPassMutation,
} from '@/generated/graphql';
import { useCreateEventPassCheckoutSessionMutation } from '@/generated/graphql';
import { formatPrice } from '@/utils/formatPrice';
import { ValuePropCard } from './ValuePropCard';
import { EventPassStatus } from '@/generated/graphql';
import { toast } from 'sonner';
import { ReactNode, useState } from 'react';

export function EventPassCard({
  eventPasses,
  price,
  event,
  onEventLive,
}: {
  eventPasses: EventPassFragment[];
  price: number;
  event: FullEventFragment;
  onEventLive: () => void;
}) {
  const availableEventPasses = eventPasses.filter(
    (eventPass) => eventPass.status === EventPassStatus.Paid,
  );
  const { data: featureFlags } = useFeatureFlagsQuery();
  const promoCode = featureFlags?.featureFlags.eventPassPromoCode;

  const [createEventPassCheckoutSession] =
    useCreateEventPassCheckoutSessionMutation();

  const [redeemEventPass] = useRedeemEventPassMutation();
  const [creatingSession, setCreatingSession] = useState(false);

  let cardHeading = 'Event Pass';
  let cardDescription: ReactNode = (
    <>
      <p>Go live with one screen for 24 hours.</p>
      {promoCode && (
        <p className="font-bold">
          Use promo code{' '}
          <code className="inline-block rounded bg-muted px-2 py-1 font-mono tracking-widest">
            {promoCode}
          </code>
        </p>
      )}
    </>
  );
  let buttonText = `Buy for ${formatPrice(price)}`;
  let buttonAction = () => {
    setCreatingSession(true);
    createEventPassCheckoutSession({
      variables: {
        data: {
          successUrl: window.location.href,
          cancelUrl: window.location.href,
        },
      },
    })
      .then(({ data }) => {
        if (data?.createEventPassCheckoutSession?.url) {
          window.location.href = data.createEventPassCheckoutSession.url;
        }
      })
      .finally(() => {
        setCreatingSession(false);
      });
  };

  if (availableEventPasses.length > 0) {
    cardHeading = 'Use Event Pass';
    cardDescription = 'You can use your existing event pass to go live.';
    buttonText = `Go Live Now!`;
    buttonAction = () => {
      redeemEventPass({
        variables: {
          eventPassId: availableEventPasses[0].id,
          eventId: event.id,
        },
      }).then(({ data }) => {
        if (data?.redeemEventPass?.eventPassRedemption) {
          onEventLive();
        } else {
          toast.error('Failed to redeem event pass');
        }
      });
    };
  }

  return (
    <ValuePropCard
      icon="🎫"
      heading={cardHeading}
      description={cardDescription}
      buttonText={buttonText}
      buttonAction={buttonAction}
      buttonDisabled={creatingSession}
      className="border-constructive bg-constructive/80"
    />
  );
}
